import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Nameset spellings as they actually appear printed on real jerseys (all
 * caps, no accented-letter shortcuts skipped - if the real jersey has an
 * accent, we keep it). Some players get more than one entry because their
 * nameset changed between jerseys (e.g. Haaland: "HAALAND" at club level,
 * "BRAUT HAALAND" on some Norway shirts).
 * @type {string[]}
 */
const NAME_SUGGESTIONS = [
  // --- Estrellas actuales ---
  'MBAPPÉ',
  'HAALAND',
  'BRAUT HAALAND',
  'VINÍCIUS JR',
  'VINI JR',
  'BELLINGHAM',
  'RODRYGO',
  'PEDRI',
  'GAVI',
  'LAMINE YAMAL',
  'MUSIALA',
  'WIRTZ',
  'KIMMICH',
  'KANE',
  'SALAH',
  'VAN DIJK',
  'ALEXANDER-ARNOLD',
  'DE BRUYNE',
  'RODRI',
  'FODEN',
  'SAKA',
  'ØDEGAARD',
  'RICE',
  'GRIEZMANN',
  'DEMBÉLÉ',
  'LEWANDOWSKI',
  'MODRIĆ',
  'VALVERDE',
  'CAMAVINGA',
  'TCHOUAMÉNI',
  'RÜDIGER',
  'COURTOIS',
  'ALISSON',
  'LAUTARO',
  'DIBU MARTÍNEZ',
  'DI MARÍA',
  'ÁLVAREZ',
  'ENZO',
  'OTAMENDI',
  'LEÃO',
  'BRUNO FERNANDES',
  'CANCELO',
  'B. SILVA',
  'BERNARDO',
  'VINI',
  'RAPHINHA',
  'CASEMIRO',
  'RICHARLISON',
  'ANTONY',
  'POGBA',
  'KANTÉ',
  'VARANE',
  'GIROUD',
  'COMAN',
  'NKUNKU',
  'THURAM',
  'FÉLIX',
  'NANI',
  'QUARESMA',
  'PEPE',
  'DYBALA',
  'ICARDI',
  'STERLING',
  'RASHFORD',
  'SANCHO',
  'GREALISH',
  'MADDISON',
  'WALKER',
  'PICKFORD',
  'TRIPPIER',
  'TONEY',
  'ROBERTSON',
  'DÍAZ',
  'NÚÑEZ',
  'GAKPO',
  'SZOBOSZLAI',
  'GÜLER',
  'HUIJSEN',
  'MASTANTUONO',

  // --- Íconos con colección en la tienda ---
  'CRISTIANO RONALDO',
  'RONALDO',
  'CR7',
  'MESSI',
  'NEYMAR JR',
  'ALEXIS',
  'VIDAL',
  'RONALDINHO',
  'RONALDO NAZÁRIO',
  'R9',
  'HENRY',
  'ZIDANE',
  'ZZ',
  'MARADONA',
  'D10S',
  'IBRAHIMOVIĆ',
  'ZLATAN',
  'KAKÁ',
  'RIVALDO',
  'BECKHAM',
  'FIGO',
  'KROOS',
  'ZAMORANO',
  'SALAS',
  'VARGAS',
  'M. FERNÁNDEZ',
  'MATÍGOL',
  'PAREDES',

  // --- Otros íconos históricos ---
  'PELÉ',
  'CRUYFF',
  'BECKENBAUER',
  'PLATINI',
  'CANTONA',
  'GIGGS',
  'SCHOLES',
  'NEVILLE',
  'KEANE',
  'VIDIĆ',
  'FERDINAND',
  'ROONEY',
  'GERRARD',
  'LAMPARD',
  'TERRY',
  'DROGBA',
  'ESSIEN',
  'FÀBREGAS',
  'XAVI',
  'INIESTA',
  'PUYOL',
  'PIQUÉ',
  'BUSQUETS',
  'SUÁREZ',
  'CAVANI',
  'ANELKA',
  'VIEIRA',
  'PIRES',
  'BERGKAMP',
  'ADEBAYOR',
  'VAN PERSIE',
  'R. CARLOS',
  'CAFU',
  'ROMÁRIO',
  'BEBETO',
  'DUNGA',
  'ADRIANO',
  'ROBINHO',
  'PATO',
  'THIAGO SILVA',
  'MARCELO',
  'DANI ALVES',
  'XABI ALONSO',
  'RAMOS',
  'CASILLAS',
  'RAÚL',
  'GUTI',
  'MORIENTES',
  'HIERRO',
  'BALE',
  'ISCO',
  'BENZEMA',
  'HAZARD',
  'LUKAKU',
  'FELLAINI',
  'WITSEL',
  'MERTENS',
  'KOMPANY',
  'LARSSON',
  'KLOSE',
  'PODOLSKI',
  'SCHWEINSTEIGER',
  'LAHM',
  'NEUER',
  'MÜLLER',
  'GÖTZE',
  'ÖZIL',
  'KHEDIRA',
  'BOATENG',
  'HUMMELS',
  'REUS',
  'GNABRY',
  'HAVERTZ',
  'GÜNDOĞAN',
  'SANÉ',
  'WERNER',
  'RUMMENIGGE',
  'MATTHÄUS',
  'KLINSMANN',
  'VÖLLER',
  'BALLACK',
  'KAHN',
  'EFFENBERG',
  'TREZEGUET',
  'DESCHAMPS',
  'PAPIN',
  'GIRESSE',
  'MATUIDI',
  'UMTITI',
  'PAYET',
  'FEKIR',
  'MARTIAL',
  'VARDY',
  'SHEARER',
  'OWEN',
  'CARRAGHER',
  'FOWLER',
  'TORRES',
  'AGÜERO',
  'TEVEZ',
  'HIGUAÍN',
  'PASTORE',
  'VERÓN',
  'CRESPO',
  'BATISTUTA',
  'SIMEONE',
  'RIQUELME',
  'AIMAR',
  'ORTEGA',
  'CANIGGIA',
  'RUI COSTA',
  'MOUTINHO',

  // --- Liga chilena actual ---
  'ARÁNGUIZ',
  'BRAVO',
  'MARIPÁN',
  'ISLA',
  'PULGAR',
  'VALDÉS',
  'OSORIO',
  'ASSADI',
  'SUAZO',
  'OPAZO',
  'CORREA',
  'BOLADOS',
  'WIEMBERG',
  'HORMAZÁBAL',
  'THOMPSON',
  'FALCÓN',
  'ZAMPEDRI',
  'BRERETON DÍAZ',
  'BRERETON',
  'POBLETE',
  'TOSELLI',
  'FERNÁNDEZ',
  'GUERRA',
  'ZAVALA',
  'SEPÚLVEDA',
  'AMPUERO',
  'CANALES',
  'MONTES',
  'TAPIA',
  'CUEVAS',
  'UREÑA',
  'ARAVENA',
  'BARTICCIOTTO',
  'ALTAMIRANO',
  'LOYOLA',
  'PINARES',
  'ECHEVERRÍA',
  'ARIAS',
  'MORA',
  'MORALES',
  'LICHNOVSKY',
  'M. NÚÑEZ',
  'PAVEZ',
  'V. PIZARRO',
  'DAMIÁN PIZARRO',
  'GIL',
  'AMOR',
  'S. RODRÍGUEZ',
  'VOZINHA',
  'CORTÉS',

  // --- Íconos históricos de la Roja ---
  'CASZELY',
  'FIGUEROA',
  'VALDIVIA',
  'H. SUAZO',
  'DAVID PIZARRO',
  'BEAUSEJOUR',
  'G. JARA',
  'PINILLA',
  'L. JIMÉNEZ',
  'LIVINGSTONE',

  // --- Más estrellas actuales ---
  'ENDRICK',
  'BRUNO GUIMARÃES',
  'EDERSON',
  'MARQUINHOS',
  'MILITÃO',
  'ALEX SANDRO',
  'DANILO',
  'FABINHO',
  'NICO GONZÁLEZ',
  'MAC ALLISTER',
  'DE PAUL',
  'MOLINA',
  'ROMERO',
  'L. MARTÍNEZ',
  'PALACIOS',
  'LO CELSO',
  'Á. CORREA',
  'FERRAN TORRES',
  'MORATA',
  'DANI OLMO',
  'MERINO',
  'NICO WILLIAMS',
  'LE NORMAND',
  'UNAI SIMÓN',
  'LAPORTE',
  'YEREMY PINO',
  'FERMÍN LÓPEZ',
  'CUBARSÍ',
  'BALDE',
  'BASTONI',
  'BARELLA',
  'DIMARCO',
  'CHIESA',
  'RETEGUI',
  'KEAN',
  'OSIMHEN',
  'LOOKMAN',
  'T. HERNÁNDEZ',
  'PULISIC',
  'R. LEÃO',
  'FRIMPONG',
  'XHAKA',
  'TAH',
  'SCHLOTTERBECK',
  'ADEYEMI',
  'OLISE',
];

/**
 * Renders the "Nombre y Número" personalization option on a product page.
 * Nombre and Número are each an independent $1.000 charge; checking
 * "Número también adelante" adds one more $1.000 unit. All charges are the
 * same $1.000 add-on product, added to the cart with quantity = unit count,
 * right after the jersey itself is added successfully.
 *
 * @typedef {Object} PersonalizationFormRefs
 * @property {HTMLInputElement} nameInput
 * @property {HTMLUListElement} nameSuggestions
 * @property {HTMLInputElement} numberInput
 * @property {HTMLLabelElement} frontBackWrapper
 * @property {HTMLInputElement} frontBackCheckbox
 * @property {HTMLInputElement} modeInput
 * @property {HTMLInputElement} chargeInput
 * @property {HTMLParagraphElement} [totalHint]
 * @property {HTMLDivElement} [liveRegion]
 *
 * @extends {Component<PersonalizationFormRefs>}
 */
class PersonalizationForm extends Component {
  /** @type {((event: any) => void) | null} */
  #cartAddEventBound = null;
  /** @type {number} */
  #activeSuggestionIndex = -1;
  /** @type {HTMLButtonElement | null} */
  #buyNowButton = null;
  /** @type {HTMLElement | null} */
  #acceleratedBlock = null;
  /** @type {MutationObserver | null} */
  #blockObserver = null;
  /** @type {((event: MouseEvent) => void) | null} */
  #outsideClickBound = null;

  requiredRefs = ['nameInput', 'nameSuggestions', 'numberInput', 'frontBackWrapper', 'frontBackCheckbox', 'modeInput', 'chargeInput'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.nameInput.addEventListener('input', this.#recalculate);
    this.refs.nameInput.addEventListener('input', this.#onNameInput);
    this.refs.nameInput.addEventListener('keydown', this.#onNameKeydown);
    this.refs.nameInput.addEventListener('focus', this.#onNameInput);
    this.refs.numberInput.addEventListener('input', this.#sanitizeNumber);
    this.refs.numberInput.addEventListener('input', this.#recalculate);
    this.refs.frontBackCheckbox.addEventListener('change', this.#recalculate);

    this.#outsideClickBound = (event) => {
      if (!this.refs.nameSuggestions.contains(/** @type {Node} */ (event.target)) && event.target !== this.refs.nameInput) {
        this.#closeSuggestions();
      }
    };
    document.addEventListener('click', this.#outsideClickBound);

    this.#recalculate();

    try {
      this.#setupBuyNow();
    } catch (error) {
      console.warn('[personalization-form] Buy-now setup failed:', error);
    }

    this.#cartAddEventBound = (event) => this.#handleCartAdd(event);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartAddEventBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.nameInput.removeEventListener('input', this.#recalculate);
    this.refs.nameInput.removeEventListener('input', this.#onNameInput);
    this.refs.nameInput.removeEventListener('keydown', this.#onNameKeydown);
    this.refs.nameInput.removeEventListener('focus', this.#onNameInput);
    this.refs.numberInput.removeEventListener('input', this.#sanitizeNumber);
    this.refs.numberInput.removeEventListener('input', this.#recalculate);
    this.refs.frontBackCheckbox.removeEventListener('change', this.#recalculate);

    this.#blockObserver?.disconnect();
    this.#blockObserver = null;
    this.#buyNowButton?.remove();
    this.#buyNowButton = null;

    if (this.#outsideClickBound) {
      document.removeEventListener('click', this.#outsideClickBound);
      this.#outsideClickBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }
  }

  /** @param {string} value */
  #normalize(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  }

  #onNameInput = () => {
    const query = this.refs.nameInput.value.trim();
    if (!query) { this.#closeSuggestions(); return; }

    const normalizedQuery = this.#normalize(query);
    const matches = NAME_SUGGESTIONS.filter((name) => this.#normalize(name).includes(normalizedQuery)).slice(0, 8);

    if (!matches.length) { this.#closeSuggestions(); return; }

    this.#renderSuggestions(matches);
  };

  /** @param {string[]} matches */
  #renderSuggestions(matches) {
    const { nameSuggestions, nameInput } = this.refs;
    this.#activeSuggestionIndex = -1;
    nameSuggestions.innerHTML = '';

    matches.forEach((name) => {
      const li = document.createElement('li');
      li.textContent = name;
      li.className = 'personalization-form__suggestion';
      li.setAttribute('role', 'option');
      li.addEventListener('mousedown', (event) => {
        // mousedown (not click) so it fires before the input's blur.
        event.preventDefault();
        this.#selectSuggestion(name);
      });
      nameSuggestions.appendChild(li);
    });

    nameSuggestions.hidden = false;
    nameInput.setAttribute('aria-expanded', 'true');
  }

  #closeSuggestions() {
    this.refs.nameSuggestions.hidden = true;
    this.refs.nameSuggestions.innerHTML = '';
    this.refs.nameInput.setAttribute('aria-expanded', 'false');
    this.#activeSuggestionIndex = -1;
  }

  /** @param {string} name */
  #selectSuggestion(name) {
    this.refs.nameInput.value = name;
    this.#closeSuggestions();
    this.#recalculate();
    this.refs.nameInput.focus();
  }

  /** @param {KeyboardEvent} event */
  #onNameKeydown = (event) => {
    const items = /** @type {HTMLLIElement[]} */ (Array.from(this.refs.nameSuggestions.children));
    if (this.refs.nameSuggestions.hidden || !items.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#activeSuggestionIndex = Math.min(this.#activeSuggestionIndex + 1, items.length - 1);
      this.#highlightSuggestion(items);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#activeSuggestionIndex = Math.max(this.#activeSuggestionIndex - 1, 0);
      this.#highlightSuggestion(items);
    } else if (event.key === 'Enter') {
      if (this.#activeSuggestionIndex >= 0 && items[this.#activeSuggestionIndex]) {
        event.preventDefault();
        this.#selectSuggestion(items[this.#activeSuggestionIndex].textContent || '');
      }
    } else if (event.key === 'Escape') {
      this.#closeSuggestions();
    }
  };

  /** @param {HTMLLIElement[]} items */
  #highlightSuggestion(items) {
    items.forEach((item, i) => {
      item.classList.toggle('personalization-form__suggestion--active', i === this.#activeSuggestionIndex);
    });
    items[this.#activeSuggestionIndex]?.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Only digits and "+" are allowed while typing, and "+" is only ever
   * allowed as part of the literal "1+8" (Zamorano's retired-number combo) -
   * every other jersey number stays plain digits.
   * @param {Event} event
   */
  #sanitizeNumber = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    let value = input.value.replace(/[^0-9+]/g, '');

    if (value.includes('+')) {
      if (!'1+8'.startsWith(value)) {
        value = value.replace(/\+/g, '').slice(0, 2);
      }
    } else if (value.length > 2) {
      value = value.slice(0, 2);
    }

    if (value !== input.value) input.value = value;
  };

  #recalculate = () => {
    const { nameInput, numberInput, frontBackWrapper, frontBackCheckbox, modeInput, chargeInput, totalHint, liveRegion } =
      this.refs;

    const hasName = nameInput.value.trim().length > 0;
    const hasNumber = numberInput.value.trim().length > 0;

    // The front+back option only makes sense once a number is entered.
    frontBackCheckbox.disabled = !hasNumber;
    if (!hasNumber && frontBackCheckbox.checked) {
      frontBackCheckbox.checked = false;
    }
    frontBackWrapper.classList.toggle('personalization-form__checkbox-label--disabled', !hasNumber);

    const hasFrontBack = hasNumber && frontBackCheckbox.checked;

    let units = 0;
    if (hasName) units += 1;
    if (hasNumber) units += 1;
    if (hasFrontBack) units += 1;

    chargeInput.value = String(units);
    this.#syncBuyNow();

    /** @type {string[]} */
    const parts = [];
    if (hasName) parts.push('Nombre');
    if (hasNumber) parts.push(hasFrontBack ? 'Número (adelante y atrás)' : 'Número (atrás)');
    modeInput.value = parts.length ? parts.join(' + ') : '';

    if (totalHint) {
      if (units > 0) {
        totalHint.textContent = `Personalización: +$${(units * 1000).toLocaleString('es-CL')}`;
        totalHint.classList.remove('hidden');
      } else {
        totalHint.textContent = '';
        totalHint.classList.add('hidden');
      }
    }

    if (liveRegion) {
      liveRegion.textContent = units > 0 ? `Recargo de personalización: $${units * 1000}` : '';
    }
  };


  /**
   * El botón nativo "Comprar ahora" (dynamic checkout) va directo al pago sin pasar
   * por el carrito, así que el cobro de personalización nunca se agregaba. Cuando hay
   * personalización lo reemplazamos por un botón propio que agrega camiseta + cobro
   * y luego envía al checkout.
   */
  #setupBuyNow() {
    const form = this.closest('form');
    const block = /** @type {HTMLElement | null} */ (form?.querySelector('.accelerated-checkout-block') ?? null);
    if (!form || !block) return;

    this.#acceleratedBlock = block;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'personalization-buy-now';
    button.textContent = 'Comprar ahora';
    button.hidden = true;
    button.addEventListener('click', this.#onBuyNowClick);
    block.insertAdjacentElement('afterend', button);
    this.#buyNowButton = button;

    this.#blockObserver = new MutationObserver(() => this.#syncBuyNow());
    this.#blockObserver.observe(block, { attributes: true, attributeFilter: ['hidden'] });

    this.#syncBuyNow();
  }

  #syncBuyNow = () => {
    const button = this.#buyNowButton;
    const block = this.#acceleratedBlock;
    if (!button || !block) return;

    const units = Number(this.refs.chargeInput.value) || 0;
    const active = units > 0;

    block.classList.toggle('personalization-hidden', active);
    button.hidden = !(active && !block.hidden);
  };

  #onBuyNowClick = async () => {
    const button = this.#buyNowButton;
    const form = this.closest('form');
    if (!button || !form) return;

    const units = Number(this.refs.chargeInput.value) || 0;
    const unitVariantId = this.dataset.unitVariantId;
    const data = new FormData(form);
    const variantId = data.get('id');
    const quantity = Math.max(1, Number(data.get('quantity')) || 1);
    if (!variantId) return;

    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    }

    /** @type {Array<{id: number, quantity: number, properties: Record<string, string>}>} */
    const items = [{ id: Number(variantId), quantity, properties }];
    if (units > 0 && unitVariantId) {
      items.push({
        id: Number(unitVariantId),
        quantity: units * quantity,
        properties: {
          Para: this.dataset.productTitle || '',
          Nombre: this.refs.nameInput.value,
          Número: this.refs.numberInput.value,
          Detalle: this.refs.modeInput.value,
        },
      });
    }

    button.disabled = true;
    button.textContent = 'Procesando...';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(String(response.status));
      window.location.href = '/checkout';
    } catch (error) {
      console.warn('[personalization-form] Buy-now failed:', error);
      button.disabled = false;
      button.textContent = 'Comprar ahora';
      window.alert('No pudimos procesar tu compra. Intenta de nuevo o usa "Agregar al carrito".');
    }
  };

  /** @returns {string | null} */
  #getMainVariantId() {
    const form = this.closest('form');
    return /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value ?? null;
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartAdd(event) {
    if (event.action !== 'add') return;

    const units = Number(this.refs.chargeInput.value) || 0;
    if (units <= 0) return;

    const mainVariantId = this.#getMainVariantId();
    if (!mainVariantId) return;

    const addedLine = event.lines?.find(
      /** @param {{merchandiseId: string, quantity: number}} line */
      (line) =>
        String(line.merchandiseId).endsWith(`/${mainVariantId}`) || String(line.merchandiseId) === String(mainVariantId)
    );
    if (!addedLine) return;

    const jerseyQuantity = addedLine.quantity || 1;
    const unitVariantId = this.dataset.unitVariantId;
    if (!unitVariantId) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        return this.#addAddonToCart(unitVariantId, units * jerseyQuantity);
      })
      .catch(() => {});
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async #addAddonToCart(variantId, quantity) {
    const properties = {
      Para: this.dataset.productTitle || '',
      Nombre: this.refs.nameInput.value,
      Número: this.refs.numberInput.value,
      Detalle: this.refs.modeInput.value,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity, properties }],
        }),
      });

      if (!response.ok) return;

      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        await customElements.whenDefined('cart-items-component');
        // @ts-ignore
        cartItemsComponent.fetchCartData?.();
        try {
          // @ts-ignore
          await sectionRenderer.renderSection(cartItemsComponent.sectionId, {
            cache: false,
            // @ts-ignore
            mode: cartItemsComponent.isDrawer ? 'hydration' : 'full',
          });
        } catch (renderError) {
          console.warn('[personalization-form] Cart refresh failed:', renderError);
        }
      }

      try {
        // El globo del carrito cuenta el cobro de personalización recién agregado.
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) {
          const cartJson = await cartResponse.json();
          for (const icon of document.querySelectorAll('cart-icon')) {
            // @ts-ignore
            icon.renderCartBubble?.(cartJson.item_count, false);
          }
        }
      } catch (bubbleError) {
        console.warn('[personalization-form] Cart bubble refresh failed:', bubbleError);
      }
    } catch (error) {
      console.warn('[personalization-form] Failed to add personalization surcharge:', error);
    }
  }
}

if (!customElements.get('personalization-form')) {
  customElements.define('personalization-form', PersonalizationForm);
}
